// ═══════════════════════════════════════════════════════════════
// WhatsNewModal — shown-once-per-device "What's New" card.
//
// Appears the first time a signed-in user opens the app after a
// release whose WHATS_NEW_VERSION they haven't seen yet. Dismiss
// (Got it / X / overlay) writes the version to localStorage so it
// never shows again until the next bump.
//
// Per-device by design (localStorage), matching the push opt-in
// device semantics. No Firestore, no server tracking — for a small
// alpha that's the right amount of plumbing.
//
// To re-surface for the next phase: bump WHATS_NEW_VERSION and
// swap the HIGHLIGHTS list. Everyone sees the new card once.
//
// Off-ramps (intentionally NOT built here): per-feature just-in-time
// tooltips and a permanent Help section. Those are later onboarding
// layers — this is only the first-run "here's what changed" card.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { X, Sparkles, Bell, ClipboardList, Camera, Gauge, Tags, Receipt } from 'lucide-react';

// Bump this string when there's a new batch worth announcing.
// localStorage stores the last version the device acknowledged.
export const WHATS_NEW_VERSION = 'phase2';
const STORAGE_KEY = 'bayless:whatsNewSeen';

// The highlights. Kept to the punchy few — a first-run card is not a
// manual. Each: icon, title, one line. Lead with the one that needs
// the user to DO something (push opt-in).
const HIGHLIGHTS = [
  {
    Icon: Bell,
    title: 'Get pinged when a customer approves',
    body: 'Your phone buzzes the second they tap Approve. Turn it on once in Settings → Enable notifications.',
  },
  {
    Icon: ClipboardList,
    title: 'Vehicle inspections with photos',
    body: 'Good / Needs attention / Replace on a 9-point check. Add the Inspection labor preset to a job to use it.',
  },
  {
    Icon: Camera,
    title: 'Job photos + notes',
    body: 'Snap photos on any job and jot a note. They ride along onto the estimate, invoice, and History.',
  },
  {
    Icon: Gauge,
    title: 'Mileage in / out',
    body: 'Odometer fields on the active job auto-save and calculate trip miles for tax time.',
  },
  {
    Icon: Tags,
    title: 'Labor presets + hourly labor',
    body: 'Save your flat-rates as one-tap chips, or price a line as hours × rate.',
  },
  {
    Icon: Receipt,
    title: 'Parts vs labor on every quote',
    body: 'Customers see the breakdown on their estimate link — they know what they are paying for.',
  },
];

function hasSeenCurrent() {
  try {
    return localStorage.getItem(STORAGE_KEY) === WHATS_NEW_VERSION;
  } catch (_) {
    // localStorage can throw in locked-down / private modes. Fail to
    // "already seen" so we never trap the user behind a card we can't
    // dismiss persistently.
    return true;
  }
}

export default function WhatsNewModal() {
  // Lazy initial state so we don't flash the card for one paint before
  // the effect runs.
  const [open, setOpen] = useState(() => !hasSeenCurrent());

  function dismiss() {
    try {
      localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION);
    } catch (_) { /* see hasSeenCurrent — best-effort */ }
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div className="modal-box whats-new-box" role="dialog" aria-modal="true" aria-labelledby="whats-new-title">
        <div className="modal-header">
          <h2 id="whats-new-title">
            <Sparkles size={18} style={{ verticalAlign: '-3px', marginRight: 6, color: 'var(--accent)' }} />
            What's New
          </h2>
          <button type="button" className="btn-link" onClick={dismiss} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p className="whats-new-intro">
          Phase 2 just landed. Here's what you can do now:
        </p>

        <ul className="whats-new-list">
          {HIGHLIGHTS.map(({ Icon, title, body }) => (
            <li key={title} className="whats-new-item">
              <span className="whats-new-icon" aria-hidden="true">
                <Icon size={18} />
              </span>
              <span className="whats-new-text">
                <strong>{title}</strong>
                <span>{body}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="form-actions">
          <button type="button" className="btn" onClick={dismiss} autoFocus style={{ width: '100%' }}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
